(ns hai.tasks.page
  (:require
   [garden.stylesheet]
   [hai.entity.data :as entity-data]
   [hai.entity.sections :as sections]
   [hai.sharing.components :as sharing.components]
   [hai.sharing.helper :as sharing]
   [hai.tags.combobox :as tag-combobox]
   [hai.tasks.assignee-picker :as assignee-picker]
   [hai.tasks.card :as task-card]
   [hai.tasks.data :as data]
   [hai.tasks.deadline :as deadline]
   [hai.tasks.eql :as tasks-eql]
   [hai.tasks.facets :as facets]
   [hai.tasks.form :as form]
   [hai.tasks.ui :as tasks-ui]
   [hai.tasks.view :as view]
   [hai.ui.components :as c]
   [hai.ui.components.card :as card]
   [hai.ui.components.collection.search :as collection-search]
   [hai.ui.components.collection.table :as collection]
   [hai.ui.components.collection.views.kanban :as kanban]
   [hai.ui.components.collection.views.table :as table]
   [hai.ui.components.form.core :as uiform]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e >e! defevent defevent-fx defsub]]
   [hai.ui.tokens :as t]
   [hai.web-app.add-menu :as add-menu]
   [hai.web-app.layout :as layout]
   [hai.web-app.routing :as routing]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private person-icon (icons/mui-icon :outlined :person))
(def ^:private open-icon (icons/mui-icon :outlined :open_in_new))
(def ^:private edit-icon (icons/mui-icon :outlined :edit))
(def ^:private share-icon (icons/mui-icon :outlined :person_add))
(def ^:private trash-icon (icons/mui-icon :outlined :delete))
(def ^:private drag-handle-icon (icons/mui-icon :outlined :drag_indicator))

(defsub ::selected [db]
  (get db ::selected #{}))

(defsub ::selected? [db task-uuid]
  (contains? (get db ::selected #{}) task-uuid))

(defevent ::toggle-selected [db task-uuid]
  (let [selected (get db ::selected #{})]
    (assoc db ::selected (if (contains? selected task-uuid)
                           (disj selected task-uuid)
                           (conj selected task-uuid)))))

(defevent ::clear-selected [db]
  (dissoc db ::selected))

(defsub ::selected-tasks
  [selected <- [::selected]
   tasks    <- [::data/list]]
  (filterv #(contains? selected (:task/uuid %)) tasks))

;; Bare mutation forms (not `{(mutation …) [out]}` joins): EQL collapses
;; several joins that share a dispatch key, so only one would run. Bare calls
;; each execute. Mirrors the shared collection bulk-action.
(defevent-fx ::bulk-complete [{:keys [db]} task-uuids]
  ;; The completed-archive collection is only mounted (and registered under
  ;; `:collections`) while its section is expanded — resolving it while
  ;; collapsed would throw "No table found".
  {:db (dissoc db ::selected)
   :resolve {:query (mapv (fn [task-uuid]
                            (list 'task/complete {:task/uuid task-uuid}))
                          task-uuids)
             :on-success-fx (cond-> [[:dispatch [::data/load-all-my-tasks]]]
                              (::completed-open? db)
                              (conj [:dispatch [:table/resolve ::completed]]))}})

(defevent-fx ::bulk-delete [{:keys [db]} task-uuids]
  {:db (dissoc db ::selected)
   :resolve {:query (mapv (fn [task-uuid]
                            (list 'task/delete {:task/uuid task-uuid}))
                          task-uuids)
             :on-success-fx [[:dispatch [::data/load-all-my-tasks]]]}})

(o/defstyled task-avatar :span
  :inline-flex :items-center :justify-center :flex-shrink-0
  {:width          "26px"
   :height         "26px"
   :border-radius  "50%"
   :background     t/--card-cream
   :color          t/--card-forest
   :font-size      "11px"
   :font-weight    "700"
   :letter-spacing "0.02em"
   :user-select    "none"}
  [:svg {:width "16px" :height "16px"}]
  ;; Current user: hollow ring instead of a fill — a shape distinction that
  ;; survives grayscale and doesn't fight the row's sage hover tint.
  [:&.me
   {:background "transparent"
    :box-shadow "inset 0 0 0 1px var(--task-accent)"
    :color      t/--task-accent}])

(defn- assignee-avatar [assignee]
  (let [me?      (and assignee (data/assignee-me? @(<s :auth/identity) assignee))
        initials (data/assignee-initials assignee)]
    [task-avatar {:title (or (data/assignee-name assignee) "Unassigned")
                  :class (when me? "me")}
     (or initials  [person-icon])]))

;; ---------------------------------------------------------------------------
;; Now / Next / Later columns — the same compact row look as the Completed
;; archive below, one column per deadline bucket.
;; ---------------------------------------------------------------------------

;; No cursor of its own — the card decides whether there is anything to press.
(o/defstyled task-row-title :span
  :block
  {:color         t/--card-forest
   :font-size     "14px"
   :line-height   "1.35"
   :overflow-wrap "anywhere"})

(o/defstyled task-row :div
  :flex :flex-row :items-center
  ;; No right padding: the more-menu / drag-handle cluster sits flush to the
  ;; card's right edge, buying horizontal room the fixed-size icon components
  ;; can't give back themselves.
  {:gap "12px" :padding "10px 0 10px 14px"}
  ;; Fixed min-height reserves room for the deadline line so cards stay the same
  ;; height whether or not the task has a deadline.
  [:.row-body
   :flex :flex-col :justify-center
   {:flex "1 1 auto" :min-width "0" :min-height "36px" :gap "2px"}]
  [view/task-deadline {:font-size "11px" :line-height "1.3"}
   [:svg {:width "13px" :height "13px"}]]
  [task-avatar {:flex-shrink "0" :margin-right "5px"}]
  ;; Cluster the more-menu and drag handle into one right-aligned group so the
  ;; row's 12px gap falls only between the assignee and this group, not between
  ;; the two controls themselves.
  [:.row-actions
   :flex :flex-row :items-center
   {:flex-shrink "0"}]
  [:.row-menu {:width "18px" :height "28px" :flex-shrink "0"}]
  ;; Whole-card drag affordance, shown at all times. The icon is a visual cue
  ;; only - the whole card is draggable - so it carries no drag listeners of its
  ;; own and is `aria-hidden` at the markup.
  [:.drag-handle
   :flex :items-center :justify-center :flex-shrink-0
   {:width  "18px"
    :margin-right "5px"
    :opacity    "0"
    :color  t/--card-forest-70
    :cursor "grab"}
   [:svg {:width "18px" :height "18px"}]]
  [:&:hover [:.drag-handle {:opacity "1"}]])

;; Card renderer for the kanban board. The collection card-render hook always
;; calls `(render collection row)` with a form-wrapped row, so we take that shape
;; directly and unwrap `:form/current-value` here - there is no separate adapter
;; shim ([HAI-001-05]; the unwrap is structural to the card hook, not removable).
(defn- task-row-view [_collection row]
  (r/with-let [!confirming? (r/atom false)
               !share-open? (r/atom false)
               !opened?     (r/atom false)]
    (let [task            (:form/current-value row)
          task-uuid       (:task/uuid task)
          description     (:task/description task)
          links           (:task/activities task)
          link-urls       (vec (keep :activity/url links))
          contacts        (:task/contacts task)
          linked-notes    (:task/notes task)
          linked-tasks    (:task/tasks task)
          tags            (:task/tags task)
          ;; Everything the resting card holds back. Tags ride on the row, but
          ;; past a few they ride as a "+N" that shows none of them, and that is
          ;; worth a press too.
          has-links?      (boolean (or (seq link-urls) (seq contacts)
                                       (seq linked-notes) (seq linked-tasks)))
          expandable?     (boolean (or has-links?
                                       (seq description)
                                       (sections/overflows? (count tags))))
          opened?         (and expandable? @!opened?)
          can-edit?       (sharing/can-edit? task)
          can-share?      (sharing/can-share? task)
          can-delete?     (sharing/owner? task)
          can-complete?   (data/can-complete? @(<s :auth/identity) task)
          confirming?     @!confirming?
          editing?        @(<s [::data/editing? task-uuid])
          saving?         (= :start @(<s [:fetch/state [::data/save-edit task-uuid]]))
          close-edit      (fn [] (>e [::data/stop-edit task-uuid]))
          completed-open? @(<s [::completed-open?])
          ;; The new :query board ([HAI-001-04]) renders from `:collections`, not
          ;; the `::data/tasks` slice these mutations optimistically patch. For
          ;; instant feedback ([HAI-001-07]) each per-task action optimistically
          ;; patches the board's rows *now* (`:table/patch-row`/`:table/remove-row`),
          ;; then this success re-resolve reconciles against server truth - the
          ;; authority that also self-heals a rejected mutation (`/resolve` answers
          ;; 200 on rejection, so `:on-success-fx` runs and re-fetches). The board
          ;; is always mounted here, so this re-resolve is unconditional (unlike
          ;; the archive's `::completed`, which is only registered while expanded,
          ;; and unlike create's cross-surface `:table/resolve-if-mounted`).
          resolve-board-fx [[:dispatch [:table/resolve data/board-collection-id]]]
          open            (fn [] (>e [:routing/navigate [:tasks/detail task]]))
          complete        (fn [_]
                             ;; Optimistic: drop the card from the board now (it is an
                             ;; open-tasks board, so a completed task leaves it), then
                             ;; reconcile. Re-resolve the board always; the archive
                             ;; `::completed` only while expanded (resolving it
                             ;; collapsed throws "No table found").
                            (>e [:table/remove-row data/board-collection-id :task/uuid task-uuid])
                            (>e [::data/complete task-uuid
                                 (cond-> resolve-board-fx
                                   completed-open? (conj [:dispatch [:table/resolve ::completed]]))]))
          save-edit       (fn [{:keys [title description urgency deadline] :as payload}]
                            (let [clear-deadline?  (and (:task/deadline-at task)
                                                        (nil? (:date deadline)))
                                  deadline-params  (deadline/value->params deadline)]
                              ;; Optimistic instant feedback on the board (mirrors the
                              ;; patch `::data/update` applies to `::tasks`): show the
                              ;; edited title/urgency/deadline now - an urgency change
                              ;; re-buckets the card's lane immediately. The success
                              ;; re-resolve below reconciles against server truth.
                              (>e [:table/patch-row data/board-collection-id :task/uuid task-uuid
                                   (cond-> {:task/title title :task/urgency urgency}
                                     (some? description) (assoc :task/description description)
                                     deadline-params     (merge deadline-params)
                                     clear-deadline?     (assoc :task/deadline-at nil
                                                                :task/deadline-all-day? nil
                                                                :task/deadline-timezone nil))]))
                            ;; Everything else the composer staged — urls, tags,
                            ;; linked entities, a freshly picked assignee — is
                            ;; `::data/save-edit`'s to fan out, non-optimistically;
                            ;; the success re-resolve reflects it.
                            ;; The board lists tasks rather than showing one, so
                            ;; the "Task saved" toast links on to this task.
                            (>e [::data/save-edit task
                                 (assoc payload
                                        :task-link {:href-fn #(routing/href :tasks/detail {:task/uuid %})
                                                    :target routing/link-target}
                                        :success-fx resolve-board-fx)]))
          ;; Pressing the card reveals what it holds; the task's own page is one
          ;; step further, so the menu is where it lives now.
          menu-actions (cond-> [["Open" (fn [_] (open)) open-icon]]
                         can-edit?   (conj ["Edit" (fn [_] (>e [::data/start-edit task-uuid (form/from-task task)])) edit-icon])
                         can-share?  (conj ["Share" (fn [_] (reset! !share-open? true)) share-icon])
                         can-delete? (conj ["Delete" (fn [_] (reset! !confirming? true)) trash-icon]))]
      (if editing?
        [form/editor-frame {:on-close close-edit}
         [form/task-form
          {:draft-id (data/edit-draft-id task-uuid)
           :task task
           :start-expanded? true
           :show-hint? true
           :saving? saving?
           :on-save save-edit
           :on-cancel #(>e [::data/cancel-edit task-uuid (form/from-task task)])
           :on-close close-edit
           :assignable? (data/can-assign? task)
           :render-assignee-picker
           (fn [{:keys [on-pick trigger]}]
             [assignee-picker/component
              {:editable? true
               :on-pick on-pick
               :label "Assign"
               :trigger trigger}])}]]
        [task-card/task-group {:class (when confirming? "confirming")}
         [task-card/task-shell
          (cond-> {:class (cond-> []
                            expandable?       (conj "raised")
                            (not expandable?) (conj "flat")
                            opened?           (conj "open"))}
            (and expandable? (not confirming?))
            (assoc :role "button"
                   :tab-index 0
                   :on-click #(swap! !opened? not)
                   :on-key-down (fn [e]
                                  (when (or (= "Enter" (.-key e)) (= " " (.-key e)))
                                    (.preventDefault e)
                                    (swap! !opened? not)))))
          [task-row
           [tasks-ui/stampbox {:task-title (:task/title task)
                               :disabled?  (not can-complete?)
                               :on-toggle  (fn [e]
                                             (.stopPropagation e)
                                             (complete e))}]
           [:div.row-body
            [task-row-title (:task/title task)]
            (when (:task/deadline-at task)
              [view/task-deadline {:task task}])
            ;; Tags only at rest. The board is scanned across its lanes, so the
            ;; links a task carries — urls, contacts, notes, tasks — wait inside
            ;; the card until it is pressed. Editing is the composer's, and it
            ;; still stages every one of them.
            (when (seq tags)
              [sections/entity-extras
               {:entity-uuid       task-uuid
                :editing?          false
                :expanded?         opened?
                :tags              tags
                :render-tag-search tag-combobox/search-slot
                :on-open-entity    entity-data/open-entity}])]
           [:div.row-actions
            {:on-click #(.stopPropagation %)}
            [assignee-picker/component
             {:task-uuid task-uuid
              :editable? can-edit?
              ;; Mirror the picker's default assign, but re-resolve the new
              ;; board too (its default dispatches `::data/assign` with no
              ;; success fx, so the :query board would miss the reassignment).
              ;; `assignee` is the staged pick — a contact or a typed email.
              :on-pick   (fn [assignee]
                           (>e [::data/assign task-uuid assignee resolve-board-fx]))
              :trigger   [assignee-avatar (:task/assigned-to task)]}]
            [:span.row-menu
             (when can-edit?
               [:<> [c/DialogRoot
                     {:open         @!share-open?
                      :onOpenChange (fn [open?]
                                      (reset! !share-open? open?)
                                      (when-not open?
                                        (>e [::data/load-all-my-tasks])))}
                     [c/DialogContent
                      [sharing.components/sharing-dialog-title]
                      [c/dialog-close-x]
                      [c/DialogDescription "People with access to this task"]
                      [sharing.components/permissions-table task]]]
                [table/more-menu-cell* menu-actions]])]
            (when can-edit?
              [:span.drag-handle {:aria-hidden true} [drag-handle-icon]])]]
          ;; Pressed open: the description and every link the task carries, in
          ;; the same sheet the home cards reveal.
          (when opened?
            [task-card/task-detail
             (when (seq description)
               [:div.task-desc description])
             (when has-links?
               [sections/entity-extras
                {:entity-uuid    task-uuid
                 :editing?       false
                 :link-urls      link-urls
                 :contacts       contacts
                 :linked-notes   linked-notes
                 :linked-tasks   linked-tasks
                 ;; The press is what reveals them, so every one shows — a "+N"
                 ;; pill inside the opened sheet would hide them all over again.
                 :expanded?      true
                 :on-open-entity entity-data/open-entity}])])]
         ;; A full-width line under the card, as the home cards ask it: a lane is
         ;; too narrow to seat the confirm beside the title.
         (when confirming?
           [task-card/delete-confirm
            [:span.prompt "Delete this task?"]
            [:button.delete-no
             {:type "button"
              :on-click (fn [_] (reset! !confirming? false))}
             "Cancel"]
            [:button.delete-yes
             {:type "button"
              :on-click (fn [_]
                          (reset! !confirming? false)
                          ;; Optimistic: drop the card now; the success re-resolve
                          ;; reconciles (and self-heals a rejected delete).
                          (>e [:table/remove-row data/board-collection-id :task/uuid task-uuid])
                          (>e [::data/delete task-uuid resolve-board-fx]))}
             "Delete"]])]))))

(o/defstyled bulk-controls-bar :div
  :flex :items-center
  {:gap "12px"}
  [:.count {:font-size "14px" :font-weight "600" :color t/--card-forest}]
  [:.clear
   {:padding "6px 10px"
    :background "transparent" :border "none"
    :color t/--card-forest-70 :font-family "inherit"
    :font-size "13px" :cursor "pointer"}
   [:&:hover {:color t/--card-forest}]])

(defn- bulk-confirm-dialog [!confirm]
  (let [{:keys [action tasks]} @!confirm
        n       (count tasks)
        delete? (= action :delete)]
    [c/AlertDialogRoot
     {:open         (some? @!confirm)
      :onOpenChange (fn [open?] (when-not open? (reset! !confirm nil)))}
     [c/AlertDialogContent
      [c/AlertDialogTitle (if delete? "Delete tasks" "Complete tasks")]
      [c/AlertDialogDescription
       (if delete?
         (str "Delete " n " task" (when (not= 1 n) "s") "? This can't be undone.")
         (str "Mark " n " task" (when (not= 1 n) "s") " complete?"))]
      [c/Flex {:justify "end" :gap "3" :mt "4"}
       [c/Button {:variant  "soft" :color "gray"
                  :on-click #(reset! !confirm nil)}
        "Cancel"]
       [c/Button {:variant  "solid"
                  :color    (if delete? "red" "green")
                  :on-click (fn []
                              (>e [(if delete? ::bulk-delete ::bulk-complete)
                                   (mapv :task/uuid tasks)])
                              (reset! !confirm nil))}
        (if delete? "Delete" "Complete")]]]]))

(defn- bulk-controls []
  (r/with-let [!confirm (r/atom nil)]
    (let [selected-tasks @(<s [::selected-tasks])
          me             @(<s :auth/identity)
          total          (count selected-tasks)
          completable    (filterv #(data/can-complete? me %) selected-tasks)
          deletable      (filterv sharing/owner? selected-tasks)]
      [bulk-controls-bar
       (when (pos? total)
         [:<>
          [:span.count (str total " selected")]
          [c/DropdownMenuRoot
           [c/DropdownMenuTrigger
            [c/Button {:variant "soft" :color "gray"}
             "Bulk actions" [c/DropdownMenuTriggerIcon]]]
           [c/DropdownMenuContent
            (when (seq completable)
              [c/DropdownMenuItem
               {:onSelect (fn [_] (reset! !confirm {:action :complete :tasks completable}))}
               (str "Mark complete (" (count completable) ")")])
            (when (seq deletable)
              [c/DropdownMenuItem
               {:color    "red"
                :onSelect (fn [_] (reset! !confirm {:action :delete :tasks deletable}))}
               (str "Delete (" (count deletable) ")")])]]
          [:button.clear {:on-click #(>e [::clear-selected])} "Clear"]
          [bulk-confirm-dialog !confirm]])])))

;; ---------------------------------------------------------------------------
;; Completed archive — server-paginated + searchable via the collection
;; component, lazily mounted so nothing loads until the section is opened.
;; ---------------------------------------------------------------------------

(def ^:private chevron-icon (icons/mui-icon :outlined :chevron_right))

(defsub ::completed-open? [db]
  (get db ::completed-open? false))

(defevent ::toggle-completed [db]
  (update db ::completed-open? not))

(defevent-fx ::uncomplete-archived [_ task-uuid]
  {:resolve {:query [{(list 'task/uncomplete {:task/uuid task-uuid})
                      [:task/uuid]}]
             :on-success-fx [[:dispatch [:table/resolve ::completed]]
                             [:dispatch [::data/load-all-my-tasks]]]}})

(defn- completed-when [completed-at]
  (when completed-at
    (.toLocaleDateString (js/Date. completed-at)
                         "en-US" #js {:month "short" :day "numeric"})))

(o/defstyled completed-title-link :span
  :cursor-pointer :block
  {:text-decoration "line-through"
   :color           t/--card-forest-70
   :font-size       "14px"
   :overflow-wrap   "anywhere"}
  [:&:hover {:color t/--card-forest}])

(o/defstyled completed-time-label :span
  {:white-space "nowrap" :font-size "13px" :color t/--card-forest-70})

(o/defstyled completed-row :div
  :flex :flex-row :items-center
  {:gap "12px" :padding "10px 14px"
   :background t/--colour-snow
   :border-radius "10px"
   :border-width "1px" :border-style "solid" :border-color t/--card-forest-10
   :transition "border-color 0.15s ease, background 0.15s ease"}
  [:&:hover {:border-color t/--card-sage :background t/--card-sage-soft-15}]
  [completed-title-link {:flex "1 1 auto" :min-width "0"}]
  [completed-time-label {:flex-shrink "0"}]
  [task-avatar {:flex-shrink "0"}])

(defn- completed-card [_collection item]
  (let [task     (:form/current-value item)
        allowed? (data/can-complete? @(<s :auth/identity) task)
        open     (fn [] (>e [:routing/navigate [:tasks/detail task]]))]
    [completed-row
     [tasks-ui/stampbox {:done?      true
                         :task-title (:task/title task)
                         :disabled?  (not allowed?)
                         :on-toggle  #(>e [::uncomplete-archived (:task/uuid task)])}]
     [completed-title-link
      {:role        "button"
       :tab-index   0
       :on-click    (fn [_] (open))
       :on-key-down (fn [e]
                      (when (or (= "Enter" (.-key e)) (= " " (.-key e)))
                        (.preventDefault e)
                        (open)))}
      (:task/title task)]
     [completed-time-label (completed-when (:task/completed-at task))]
     [assignee-picker/component
      {:task-uuid (:task/uuid task)
       :editable? allowed?
       :trigger   [assignee-avatar (:task/assigned-to task)]}]]))

(o/defstyled completed-wrap :div
  [card/solution-grid {:grid-template-columns "1fr" :gap "8px"}])

(defn- completed-collection []
  [completed-wrap
   [collection/component
    {:id           ::completed
     :query        [{:user/completed-tasks
                     [{:collection/items
                       [:task/uuid :task/title :task/completed-at
                        :task/assigned-to :user.entity/permissions :user.entity/owner?]}]}]
     :paginate?    true
     :pagination   {:table/per-page 10 :direction :desc :sort-attrs [:task/completed-at]}
     :default-view :collection.view/cards
     :controls     (fn [table] [collection-search/component table])
     :card         completed-card
     :no-items     (fn [_] [c/Text {:color "gray"} [:em "No completed tasks yet."]])
     :columns      []}]])

(o/defstyled completed-toggle :button
  :flex :items-center :cursor-pointer
  {:gap         "8px"
   :margin-top  "36px"
   :background  "transparent"
   :border      "none"
   :padding     "0"
   :font-family "inherit"
   :font-size   "18px"
   :font-weight "700"
   :color       t/--card-forest}
  [:svg {:width "22px" :height "22px" :fill t/--card-forest-70
         :transition "transform 0.15s ease"}]
  [:&.open [:svg {:transform "rotate(90deg)"}]])

(defn- completed-section []
  (let [open? @(<s [::completed-open?])]
    [:section
     [completed-toggle {:class (when open? "open")
                        :on-click #(>e [::toggle-completed])}
      [chevron-icon] "Completed"]
     (when open?
       [:div {:style {:margin-top "16px"}}
        [completed-collection]])]))

;; ---------------------------------------------------------------------------
;; New tasks board ([HAI-001-04]) — the bespoke Now/Next/Later columns ported
;; onto the generic `:collection.view/kanban` view. It is a `:query` collection
;; so the collection's own (server-side) search drives it, formalising what
;; `::data/load-all-my-tasks` already did (a 500-row open-tasks fetch, newest
;; first). Rendered ALONGSIDE the bespoke board below for the HITL parity
;; review; the bespoke board, its search, and the `task-row-view` card adapter
;; are removed only after sign-off ([HAI-001-05]).
;; ---------------------------------------------------------------------------

(def ^:private board-lanes
  "Now / Next / Later columns for the tasks board (mirrors `data/bucket-tabs`).
   Later is the catch-all, so untriaged / nil-urgency tasks land there exactly
   as the bespoke board does through `data/task-bucket`."
  [{:collection.view.kanban.lane/id :now   :collection.view.kanban.lane/label "Now"
    :collection.view.kanban.lane/background t/--lane-surface}
   {:collection.view.kanban.lane/id :next  :collection.view.kanban.lane/label "Next"
    :collection.view.kanban.lane/background t/--lane-surface}
   {:collection.view.kanban.lane/id :later :collection.view.kanban.lane/label "Later"
    :collection.view.kanban.lane/background t/--lane-surface
    :collection.view.kanban.lane/catch-all? true}])

;; Filter values are set from the facet sidebar ([[hai.tasks.facets/panel]]) and
;; cleared from the header chips; no per-column filter widget ever renders. The
;; collection's Filter schema still requires a `:field`, hence this stub (mirrors
;; the notes wall's `sidebar-only-filter-field`).
(defn- sidebar-only-filter-field
  [_field-props]
  nil)

(defn- assignee-summary
  "Chip face for the assignee filter: the chosen assignee's name, looked up by
   uuid in the loaded facet options, or the `:unassigned` sentinel's label."
  [current-value _column]
  (if (keyword? current-value)
    "Unassigned"
    (or (some (fn [{uuid :user/uuid full-name :user/full-name}]
                (when (= uuid current-value) full-name))
              (:facet/assignees @(<s [::data/facets])))
        "Anyone")))

(defn- tags-summary
  "Chip face for the tag filter: the chosen tag, `#`-prefixed so it reads as a
   tag (`#work`), or the `:untagged` sentinel's label. The value is a one-element
   name vector `[tag-name]` or the `:untagged` keyword (see the facet sidebar)."
  [current-value _column]
  (cond
    (keyword? current-value) "Untagged"
    (first current-value)    (str "#" (first current-value))
    :else                    "Any tag"))

(o/defstyled board-search :div
  "Brand theming for the generic collection search on the tasks board so it reads
   as a task-token twin of the notes wall's header search ([HAI-CHROME-02]).
   Consumer-side by design: the brand tokens (forest / sage / snow) stay here in
   web-app and the shared `collection/search` component keeps its neutral
   design-system look - this wrapper just restyles the radix `TextField`
   descendants that component renders. It neutralises the radix surface variant's
   own box-shadow outline and 2px focus ring, then applies a real 1px sage border
   in the task palette: a visible `--card-sage-light` resting edge that deepens to
   `--card-sage` on focus, mirroring the notes search's `note-sage-35`/`-55`
   two-step. Sizing (13px text, 340px basis, 7px/12px padding) matches the notes
   header search so the two read as the same control in two tints."
  ;; Standalone spacing to the lanes when the search is not hosted in the sticky
  ;; header (the header zeroes this); mirrors the collection wrapper's own gap.
  ;; This wrapper is the header's flex child (as the notes `input.header-search`
  ;; is its own), so the 340px basis lives here - on the inner radix root it would
  ;; collapse, the wrapper being an `auto`-basis block that content-sizes.
  {:margin-bottom "16px"
   :flex          "0 1 340px"
   :min-width     "160px"}
  [:.rt-TextFieldRoot
   {:width         "100%"
    ;; radix size-2 pins the root to 32px; let padding + the input's line-height
    ;; drive the ~36px height, matching the notes header search.
    :height        "auto"
    :padding       "7px 12px"
    :border-width  "1px"
    :border-style  "solid"
    :border-color  t/--card-sage-light
    :border-radius "8px"
    :background    t/--colour-snow
    :box-shadow    "none"
    ;; radix draws a 2px focus outline in its accent colour; suppress it and let
    ;; the sage border be the only focus affordance, as the notes input does.
    :outline       "none"
    :transition    "border-color 0.15s ease"}
   [:&:focus-within {:border-color t/--card-sage}]]
  [:.rt-TextFieldInput
   {;; radix ships the input a 0.5px bottom padding; zero it (the 7px/12px lives
    ;; on the root) so the box height stays on the notes search's ~36px.
    :padding     "0"
    :color       t/--card-forest
    :font-size   "13px"
    :font-family "inherit"}
   [:&::placeholder {:color t/--card-forest-70}]])

;; ---------------------------------------------------------------------------
;; Sticky page header — the notes wall's header shell ([[hai.notes.page]]),
;; retinted in the board's task tokens (the forest / sage / snow twins the facet
;; surfaces already wear). Hosts the board's EXISTING pieces — the collection
;; search (brand-styled `board-search` around `collection-search/component`) and
;; the `facets/applied-chips` — plus the actions cluster: Add-task, the filter
;; funnel, and the ⋮ view-settings popover.
;; ---------------------------------------------------------------------------

(def ^:private board-icon (icons/mui-icon :outlined :checklist))

(o/defstyled task-page-header :header
  {:position "sticky" :top "0" :z-index "50"
   :display "flex" :align-items "center" :flex-wrap "wrap"
   :gap "10px"
   :padding "10px 14px"
   :background t/--tasks
   :border-bottom-width "2px" :border-bottom-style "dotted"
   :border-bottom-color t/--card-sage
   :border-radius "10px"}
  [:.header-icon
   {:display "inline-flex" :align-items "center"
    :color t/--card-forest}]
  [:.header-title
   {:font-size "16px" :font-weight "700" :line-height "1.2"
    :margin "0" :color t/--card-forest}]
  ;; The search's own bottom margin (its spacing to the lanes on the bespoke
  ;; board) is redundant in the header row, so zero it here.
  [board-search {:margin-bottom "0"}]
  ;; A display:contents slot keeps the chips inline without an extra box, so the
  ;; header children hold fixed positions and the settings form never remounts.
  [:.chips-slot {:display "contents"}]
  [:.header-actions
   {:display "flex" :align-items "center" :gap "4px" :margin-left "auto"}]
  [:.header-btn
   {:width "28px" :height "28px"
    :border "none" :border-radius "999px"
    :background "transparent" :padding "0"
    :color t/--card-forest :cursor "pointer"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :transition "background 0.15s ease"}
   [#{:&:hover :&.active} {:background t/--card-forest-10}]
   [:svg {:width "16px" :height "16px" :fill "currentColor"}]
   ;; Labeled variant (the filter + add buttons): icon + text, sized to content.
   ;; The bare ⋮ settings button keeps the round icon-only base.
   [:&.labeled
    {:width "auto" :gap "6px" :padding "0 12px"
     :font-family "inherit" :font-size "13px" :font-weight "600"}]
   ;; Filled "Add task" button — the tasks brand sage. Its own hover keeps the
   ;; fill (the shared header-btn hover would blank it back to transparent).
   [:&.add
    {:background t/--task-add-fill}
    [#{:&:hover :&.active} {:background t/--task-add-fill :filter "brightness(0.95)"}]]])

;; The ⋮ popover's rows — the collection settings form's own look, narrowed to a
;; single row (Case sensitive). No Admin view (low value here) and no view-mode
;; select: the board is kanban-only, so a view switcher would break that contract.
(o/defstyled settings-rows :div
  :flex :flex-col :gap-2
  [:& :> [:div :flex :flex-row :items-center :gap-2]])

(defn- header-controls
  "`:form/render` for the collection's settings sub-form, mounted in the header's
   actions cluster. The funnel toggles the sidebar via the `:show-filters-table?`
   setting — the single show-filters flag a single-view kanban board needs (the
   same flag the kanban view and the page layout gate on). The Add-task button
   opens the shared add-task modal. The ⋮ popover exposes only `case-sensitive?`,
   which the board's server-side `:user.tasks/table` search honours."
  [{fields :form/fields}]
  (let [flag        (get fields :show-filters-table?)
        filters-on? (boolean (:form.field/current-value flag))
        toggle!     (fn [] ((:form.field/set-value! flag) (not filters-on?)))]
    [:<>
     [c/Tooltip {:content (if filters-on? "Hide filters" "Show filters")}
      [:button.header-btn
       {:type       "button"
        :class      ["labeled" (when filters-on? "active")]
        :aria-label (if filters-on? "Hide filters" "Show filters")
        :on-click   toggle!}
       [icons/filled-filter_alt]
       [:span "Filter"]]]
     [c/Tooltip {:content "Add task"}
      [:button.header-btn
       {:type       "button"
        :class      ["labeled" "add"]
        :aria-label "Add task"
        :on-click   #(>e [::add-menu/open-task-modal])}
       [icons/plus {:size 16}]
       [:span "Add Task"]]]
     [c/PopoverRoot
      [c/PopoverTrigger
       [:button.header-btn {:type "button" :aria-label "Tasks view settings"}
        [icons/filled-more_vert]]]
      [c/PopoverContent {:align "end"}
       [settings-rows
        [:label
         [:div
          [uiform/checkbox (get fields :case-sensitive?)]
          [c/Text {:title "search, filtering"} "Case sensitive"]]]]]]]))

(defn- page-chrome
  "`:controls` for the tasks board — the sticky header: task icon + title, the
   existing collection search, the applied-filter chips, and the actions cluster
   (mounted as the collection's `:table/settings` sub-form so the funnel and the
   case-sensitive toggle ride the same persistence + reload plumbing notes uses)."
  [table]
  (let [settings-field (get-in table [:table/form :form/fields :table/settings])]
    [task-page-header
     [:span.header-icon [board-icon]]
     [:h1.header-title "Tasks"]
     [board-search [collection-search/component table]]
     [:span.chips-slot [facets/applied-chips table]]
     [:div.header-actions
      (when settings-field
        [uiform/component (assoc settings-field
                                 :form/render header-controls
                                 ;; A remount must never re-seed the sub-form —
                                 ;; the live funnel/case-sensitive state lives there.
                                 :form/suppress-setup-if-done? true)])]]))

;; ---------------------------------------------------------------------------
;; Board layout - the facet sidebar in a left column beside the lanes. Mirrors
;; the notes wall's `wall-layout`: a grid applied to the collection root, whose
;; flat children (the sticky header, the facet `sidebar`, the kanban `board`) are
;; placed into a 240px sidebar column + a flexible lanes column. The sidebar is
;; shown or hidden by the header's funnel toggle, via the `:show-filters-table?`
;; setting: base is a single lanes column (the kanban view renders no sidebar
;; sibling when the flag is off); `.with-sidebar` adds the 240px facet column
;; when it is on (default shown - the board declares `:show-filters-default? true`
;; since it is kanban-only and wants the facets visible from the start).
;; Collapses to a single stacked column on narrow screens.
;; ---------------------------------------------------------------------------

(o/defstyled board-layout :div
  [collection/component
   {:display               "grid"
    :grid-template-columns "minmax(0, 1fr)"
    :gap                   "16px"
    :align-items           "start"}]
  [:&.with-sidebar
   [collection/component
    {:grid-template-columns "240px minmax(0, 1fr)"}
    [task-page-header {:grid-column "1 / -1"}]
    [facets/sidebar {:grid-column "1" :grid-row "2"}]
    [kanban/board {:grid-column "2" :grid-row "2"}]]]
  [:at-media {:max-width "768px"}
   [:&.with-sidebar
    [collection/component
     {:grid-template-columns "minmax(0, 1fr)"}
     [#{task-page-header facets/sidebar kanban/board}
      {:grid-column "1" :grid-row "auto"}]]]])

;; ---------------------------------------------------------------------------
;; Loading skeleton — a resting-task-shaped placeholder shown once per lane
;; while the board's first resolve is in flight (the kanban view renders it for
;; a still-loading `nil` lane, in place of the `Nothing here.` empty state).
;; ---------------------------------------------------------------------------

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules task-skeleton-pulse
  (garden.stylesheet/at-keyframes
   :hai-task-skeleton-pulse
   ["0%"   {:background-color c/--gray-3}]
   ["50%"  {:background-color c/--gray-5}]
   ["100%" {:background-color c/--gray-3}]))

;; A resting board card, box-for-box: `task-shell`'s flat surface wrapping
;; `task-row`'s layout, with the stamp, title and assignee replaced by blocks
;; whose background pulses. The metrics are copied from the real components so
;; the placeholder occupies the exact card box — stampbox 34×34 (`ui/stampbox`),
;; `.row-body` min-height 36px, row padding `10px 0 10px 14px`, avatar 26px — and
;; the layout does not shift when real cards replace it (total height 56px).
;; Passed to the kanban view as `:collection.view.kanban/skeleton`.
(o/defstyled task-skeleton :div
  {:background    t/--task-card-surface-flat
   :border-radius "13px"}
  [:.row
   :flex :flex-row :items-center
   {:gap "12px" :padding "10px 0 10px 14px"}]
  [:.pulse {:animation "hai-task-skeleton-pulse 1.4s ease-in-out infinite"}]
  [:.stamp {:width "34px" :height "34px" :border-radius "8px" :flex "0 0 auto"}]
  ;; Matches `.row-body`: carries the card's min-height, so the box is 56px tall
  ;; whether or not the title bar alone would fill it.
  [:.body
   :flex :flex-col :justify-center
   {:flex "1 1 auto" :min-width "0" :min-height "36px"}]
  [:.title {:height "14px" :max-width "70%" :border-radius "7px"}]
  [:.avatar {:width "26px" :height "26px" :border-radius "50%" :flex "0 0 auto"
             :margin-right "5px"}]
  ([]
   [:<>
    [:div.row
     [:div.pulse.stamp]
     [:div.body [:div.pulse.title]]
     [:div.pulse.avatar]]]))

(defn- tasks-board
  "The tasks board as a generic `:query` kanban collection. Reproduces
   `::data/load-all-my-tasks`'s fetch — open tasks only, newest first, capped at
   500 — with the full `tasks-eql/task-eql` under `:collection/items` so avatars,
   permissions, deadlines and urgency bucketing all resolve. `data/task-bucket`
   buckets into the declared lanes; `task-row-view` is the card renderer - it
   takes the form-wrapped row straight from the card-render hook and unwraps it
   itself (no separate adapter)."
  []
  [collection/component
   {:id           data/board-collection-id
    :query        [{(list :user.tasks/table {:filters {:task/open? true}})
                    [{:collection/items tasks-eql/task-eql}]}]
    :pagination   {:table/per-page 500 :sort-attrs [:task/created-at] :direction :desc}
    :default-view :collection.view/kanban
    :view-options [kanban/view-option]
    :collection.view.kanban/lanes     board-lanes
    :collection.view.kanban/bucket-fn data/task-bucket
    ;; Task-shaped loading placeholder shown per lane until the first resolve
    ;; lands (see [[task-skeleton]]).
    :collection.view.kanban/skeleton  [task-skeleton]
    ;; Drag-to-retriage ([HAI-003-04]): the generic spine calls this on a real
    ;; lane change (same-lane drops are already a no-op via `kanban/move-decision`).
    ;; Map the destination lane to a `:task/urgency` - Later writes the *explicit*
    ;; `:task.urgency/later` so a previously-untriaged task is now triaged there -
    ;; then reuse the `save-edit` template: patch the board's row now for instant
    ;; re-bucketing, and run `::data/update` whose success re-resolve reconciles
    ;; against server truth (also the 001-07 Option-A self-heal - no new error
    ;; plumbing). `::data/update` is the production `task/update` transport, which
    ;; enforces edit permission server-side, so a non-editable card (already
    ;; non-draggable via the spine's `sharing/can-edit?` gate) is safe here too.
    :collection.view.kanban/on-card-move
    (fn [{:keys [row to-lane-id]}]
      (let [uuid    (:task/uuid row)
            urgency (case to-lane-id
                      :now   :task.urgency/now
                      :next  :task.urgency/next
                      :later :task.urgency/later)]
        ;; Synchronous (`>e!`) so the card is re-bucketed into the destination
        ;; lane *within* the drop's `onDragEnd`, before the source card un-dims
        ;; from its dragging placeholder. An async patch lands a tick later, so
        ;; the source card first snaps back to full opacity in its original lane
        ;; (a visible flash) and only then relocates. The server round-trip
        ;; (`::data/update`) stays async - only the local re-bucket is sync.
        (>e! [:table/patch-row data/board-collection-id :task/uuid uuid
              {:task/urgency urgency}])
        (>e [::data/update {:task-uuid uuid :urgency urgency}
             [[:dispatch [:table/resolve data/board-collection-id]]]])))
    :card         task-row-view
    ;; The sticky page header ([[page-chrome]]) - hosts the search, applied
    ;; chips, Add-task, the filter funnel and the ⋮ view-settings popover.
    :controls     page-chrome
    :search       {:placeholder "Search tasks…"}
    ;; Show the facet sidebar by default (the board is kanban-only, so the funnel
    ;; in the header is the only place to hide it - it should start visible).
    :show-filters-default? true
    ;; Assignee facet filter ([HAI-006-03/04]): the value is set from the facet
    ;; sidebar and shown in the header chips; no per-column widget renders.
    ;; `:sibling` mounts the sidebar as a sibling of the lane grid (a board has
    ;; no in-view header row to host it), and `board-layout` positions it in the
    ;; left column. Mirrors the notes wall.
    :filters/render    facets/panel
    :filters/placement :sibling
    :columns
    [;; Value is a one-element tag-name vector `[tag-name]` OR the `:untagged`
     ;; sentinel keyword. `:any` (not `[:or [:vector :string] :keyword]`) on
     ;; purpose, same reason as the assignee column: a compound schema trips the
     ;; form's default/field instantiation and drops the key. Fully
     ;; frontend-controlled (see the facet sidebar), so `:any` is safe. Declared
     ;; first so its applied chip leads, matching the notes wall + facet order.
     {:header ""
      :attr   :task/tags
      :sort?  false
      :render (fn [_] nil)
      :filter {:schema  :any
               :type    :filter/select
               :field   sidebar-only-filter-field
               :label   "Tag"
               :icon    icons/outlined-label
               :summary tags-summary}}
     {:header ""
      :attr   :task/assigned-to
      :sort?  false
      :render (fn [_] nil)
      ;; Value is a single `:user/uuid` OR the `:unassigned` sentinel keyword.
      ;; `:any` (not `[:or :uuid :keyword]`) on purpose, mirroring the notes
      ;; wall's frontend-controlled filters: a compound schema trips the form's
      ;; default/field instantiation and drops the key. The value is fully
      ;; frontend-controlled (see the facet sidebar), so `:any` is safe.
      :filter {:schema  :any
               :type    :filter/select
               :field   sidebar-only-filter-field
               :label   "Assignee"
               :icon    icons/outlined-person
               :summary assignee-summary}}]}])

(defn page [_route]
  ;; The sticky header now carries the title and Add-task control, so the generic
  ;; platform chrome (breadcrumbs / heading / lead*) is gone - parity with the
  ;; notes wall. `sidebar?` gates the layout's `.with-sidebar` grid on the
  ;; header funnel's `:show-filters-table?` setting (default shown), mirroring
  ;; `notes.page/page`.
  (let [settings @(<s [:form.field/current-value data/board-collection-id :table/settings])
        sidebar? (boolean (:show-filters-table? settings))]
    [layout/page
     [board-layout {:class (when sidebar? "with-sidebar")}
      [tasks-board]]
     ;; The bulk-selection subsystem is dormant/dead ([HAI-001-05] reports it;
     ;; deletion is deferred to another doc). Its only mount point was the bespoke
     ;; `controls-row`; keep it mounted here so it is neither deleted nor left an
     ;; unreferenced orphan - it renders nothing until `::selected` is populated
     ;; (which no UI does).
     [bulk-controls]
     [completed-section]]))
